import { Link, useParams } from "react-router-dom";
import { CATEGORIES, getCategory } from "@/mocks/categories";
import { ARTICLES } from "@/mocks/articles";
import { FeedCard, MagazineCard } from "@/components/ui/ArticleView";
import Breadcrumb from "@/components/ui/Breadcrumb";
import SectionHeading from "@/components/ui/Section";
import { useJsonLd, usePageMeta } from "@/lib/site";

export default function CategoryPage() {
  const { key = "" } = useParams();
  const cat = getCategory(key);

  const list = cat
    ? ARTICLES.filter((a) => a.category === cat.key).sort((a, b) =>
        a.date < b.date ? 1 : -1
      )
    : [];
  const lead = cat ? list.find((a) => a.hot) ?? list[0] : undefined;
  const others = CATEGORIES.filter((c) => c.key !== key);

  usePageMeta(
    cat
      ? `${cat.name} - ${cat.source}爆料 - 明星八卦圈内`
      : "栏目不存在 - 明星八卦圈内",
    cat
      ? `${cat.name}：${cat.desc} 汇集${cat.source}的一手爆料与圈内观察，每日更新热瓜。`
      : "你访问的栏目不存在。"
  );

  const ld = cat
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "CollectionPage",
            name: `${cat.name} - ${cat.source}爆料`,
            description: cat.desc,
            url: `https://yslcare.cn/category/${cat.key}`,
            inLanguage: "zh-CN",
            mainEntity: {
              "@type": "ItemList",
              itemListElement: list.map((a, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: a.title,
                url: `https://yslcare.cn/article/${a.slug}`,
              })),
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://yslcare.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: cat.name,
                item: `https://yslcare.cn/category/${cat.key}`,
              },
            ],
          },
        ],
      }
    : null;
  useJsonLd(ld);

  if (!cat) {
    return (
      <div className="container-page py-24 text-center">
        <p className="font-heading text-4xl font-black text-primary-500">404</p>
        <p className="mt-3 text-foreground-600">这个栏目不存在或已下线。</p>
        <Link
          to="/"
          className="c-btn mt-6 bg-primary-500 text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* 栏目头图 */}
      <section className="container-page pt-6 md:pt-8">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: cat.name }]}
        />
        <div className="relative mt-4 flex min-h-[300px] flex-col justify-end overflow-hidden rounded-lg md:min-h-[340px]">
          <img
            src={cat.image}
            alt={`${cat.name} ${cat.source} 栏目头图`}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/35 to-foreground-950/10"></div>
          <div className="relative z-10 p-6 md:p-8">
            <span className="inline-flex items-center gap-1.5 rounded-sm bg-primary-500 px-2.5 py-1 text-xs font-bold text-background-50">
              <i className={cat.icon}></i>
              {cat.source} 栏目
            </span>
            <h1 className="mt-3 font-heading text-3xl font-black text-background-50 md:text-5xl">
              {cat.name}
            </h1>
            <p className="mt-2 font-heading text-accent-200 md:text-lg">
              {cat.slogan}
            </p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200/90">
              {cat.desc}
            </p>
          </div>
        </div>
      </section>

      <section className="container-page mt-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {lead && (
              <div className="mb-8">
                <SectionHeading
                  kicker="Hot Story"
                  title="本栏目精选"
                  link={`/article/${lead.slug}`}
                  linkLabel="阅读全文"
                />
                <MagazineCard a={lead} />
              </div>
            )}
            <SectionHeading
              kicker={`${list.length} Articles`}
              title={`${cat.name} · 全部内容`}
            />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {list.map((a) => (
                <FeedCard key={a.slug} a={a} />
              ))}
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className={cat.icon}></i>
                </span>
                其他圈内栏目
              </h3>
              <ul className="mt-4 space-y-3">
                {others.map((c) => (
                  <li key={c.key}>
                    <Link
                      to={`/category/${c.key}`}
                      className="group flex items-center justify-between rounded-md border border-background-200 bg-background-50 px-3 py-2.5 transition-colors hover:border-primary-300"
                    >
                      <span className="flex items-center gap-2 text-sm text-foreground-800">
                        <i className={`${c.icon} text-primary-500`}></i>
                        {c.name}
                        <span className="text-xs text-foreground-400">
                          · {c.source}
                        </span>
                      </span>
                      <i className="ri-arrow-right-line text-foreground-400 transition-transform group-hover:translate-x-1"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}